import { inject } from '@angular/core';
import { type CanActivateChildFn, Router } from '@angular/router';
import { SupabaseService } from '@app/core/supabase.service';
import { SessionService, RequiredAction } from '@app/core/session.service';

/**
 * Redirects authenticated users to onboarding if the backend says there are required steps.
 *
 * Design goals:
 * - Server is source-of-truth for "what is required" (create_workspace, confirm_tos, etc).
 * - Works on first login, later logins, and hard refresh.
 * - Avoids coupling onboarding gating to workspace-scoped context (no URL scoping needed).
 */
export const requiredActionsGuard: CanActivateChildFn = async (_route, state) => {
  const router = inject(Router);
  const supabase = inject(SupabaseService);
  const sessionService = inject(SessionService);


  // AuthGuard handles redirect-to-login; we only gate *authenticated* users.
  const { data: { session } } = await supabase.client.auth.getSession();
  if (!session) return true;

  const url = state.url;
  const isOnboardingRoute = url.startsWith('/app/onboarding');

  // Allow a small set of routes even when onboarding is required (avoid trapping users).
  // Also allow Organizations list so users who skip onboarding can land there.
  const allowWhenOnboardingRequired =
    isOnboardingRoute ||
    // url.startsWith('/app/organizations') ||
    url.startsWith('/app/support') ||
    url.startsWith('/app/contact-us') ||
    url.startsWith('/app/checkout-result');

  const sessionData = await sessionService.ensureSessionDataLoaded();
  if (!sessionData) {
    alert('in required actions guard, session data is not loaded');
    // Fail open: if session-data can't be loaded for some reason, don't brick navigation.
    console.warn('RequiredActionsGuard: session data not loaded; allowing navigation');
    return true;
  }
  const requiredActions = sessionService.requiredActions();
  console.log('requiredActions', requiredActions);
  const needsOnboarding = requiredActions.length > 0;
  
  if (needsOnboarding && !allowWhenOnboardingRequired) {
    console.log('redirecting to onboarding', needsOnboarding);
    return router.createUrlTree(['/app/onboarding'], {
      queryParams: { returnUrl: url }
    });
  }

  // If user is fully onboarded, keep onboarding route non-sticky.
  if (!needsOnboarding && isOnboardingRoute) {
    return router.createUrlTree(['/app/home']);
  }

  return true;
};


